/**
 * **一条持仓**（台账行 · 批 `99` B2）
 *
 * 依据：`投资域PRD` v2 §4.2 ②（每行七项 + 行内四个动作）· `AC-INV7`（已清仓灰显、
 * 浮盈率显示 `—`）· §5（动作文案随品种变）· `DR-059` D6（改价是本域唯一能改市值的入口）。
 *
 * ## 🔴 本组件零计算
 *
 * 市值 / 浮盈 / 浮盈率 / 占比**全部取 `HoldingView` 的现成字段**（服务端算好）——
 * 端侧任何一次 `reduce` 都会让页面数字与接口值分叉（`AC-INV13`）。
 *
 * ## 🔴 已清仓的行为什么还留着
 *
 * `quantity = 0` 但 `realized_pnl` 可能有值（`AC-INV7`：清仓不删行）。
 * 直接隐藏会让"我卖过的基金去哪了"无从回答；显示成正常行又会让人以为还持有。
 * ⇒ 灰显 + 「已清仓」chip + 浮盈率 `—`（不是 `0%`）。
 */

import type { ReactElement } from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import type { HoldingView } from '@jiaying/shared';
import {
  actionsOf,
  hasMaturity,
  holdingSubtitleOf,
  holdingTitleOf,
  isCleared,
  kindLabelOf,
  maturityTextOf,
  priceTextOf,
  qtyTextOf,
  returnTextOf,
  weightTextOf,
} from '../features/investment.js';
import { BTN_HISTORY, CLEARED_TEXT, COL_COST_PRICE, COL_PRICE, COL_QUANTITY, COL_WEIGHT } from '../features/investment-labels.js';
import { Amount } from './Amount.js';

export interface HoldingRowProps {
  view: HoldingView;
  /** 账本本地日历日（算到期剩余天数用） */
  today: string;
  onBuy: () => void;
  onSell: () => void;
  onIncome: () => void;
  onPrice: () => void;
  /** 🆕 批 127：打开该持仓的交易记录（撤销入口） */
  onHistory: () => void;
}

export function HoldingRow({ view, today, onBuy, onSell, onIncome, onPrice, onHistory }: HoldingRowProps): ReactElement {
  const cleared = isCleared(view);
  const actions = actionsOf(view.kind);
  /**
   * 到期只对**有到期概念的品种**（理财 / 存款 / 债券）显示；
   * 🔴 未填时也必须显示「到期日未填」—— 空着不写会让人以为"不到期"（PRD §11 的处理）。
   */
  const maturity = hasMaturity(view.kind) ? maturityTextOf(view.maturityDate, today) : '';

  return (
    <Box
      data-testid={`holding-row-${view.id}`}
      sx={{
        px: 2,
        py: 1.25,
        // 已清仓灰显（不是隐藏）—— 见文件头
        opacity: cleared ? 0.6 : 1,
        '&:not(:last-of-type)': { borderBottom: 1, borderColor: 'divider' },
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        {/* 左：名称 / 代码 / 品种 / 到期 · 份额 × 价格 */}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
              {holdingTitleOf(view)}
            </Typography>
            <Chip size="small" variant="outlined" label={kindLabelOf(view.kind)} />
            {cleared && (
              <Chip
                size="small"
                color="default"
                label={CLEARED_TEXT}
                data-testid={`holding-cleared-${view.id}`}
              />
            )}
          </Stack>

          <Typography variant="caption" color="text.secondary" display="block">
            {holdingSubtitleOf(view)}
            {maturity === '' ? '' : ` · ${maturity}`}
          </Typography>

          {/* 🔴 份额 / 成本价 / 现价三件都来自接口（价格恒 4 位、份额去尾零 —— 内核格式化） */}
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            data-testid={`holding-qty-${view.id}`}
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {COL_QUANTITY} {qtyTextOf(view)} · {COL_COST_PRICE} {priceTextOf(view.costPriceE4)} ·{' '}
            {COL_PRICE} {priceTextOf(view.currentPriceE4)}
          </Typography>
        </Box>

        {/* 右：市值 / 浮盈 + 浮盈率 / 占比 */}
        <Stack sx={{ alignItems: 'flex-end', flexShrink: 0 }} spacing={0.25}>
          <Amount
            cents={view.marketValueCents as never}
            currency={view.currency}
            variant="body2"
            sx={{ fontWeight: 600 }}
          />
          <Stack direction="row" spacing={0.75} alignItems="baseline">
            <Amount
              cents={view.unrealizedCents as never}
              currency={view.currency}
              signed
              variant="caption"
              // 已清仓时浮盈恒 0（无持仓）⇒ 不加符号噪音
              colored={!cleared}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid={`holding-return-${view.id}`}
            >
              {returnTextOf(view.unrealizedReturnE4)}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {COL_WEIGHT} {weightTextOf(view.weightE4)}
          </Typography>
        </Stack>
      </Stack>

      {/* 行内动作 —— 文案随品种变（PRD §5：申购/赎回 vs 买入/卖出 vs 存入/取出） */}
      <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
        <Button size="small" onClick={onBuy} data-testid={`holding-buy-${view.id}`}>
          {actions.buy}
        </Button>
        <Button
          size="small"
          onClick={onSell}
          disabled={cleared}
          data-testid={`holding-sell-${view.id}`}
        >
          {actions.sell}
        </Button>
        {actions.incomeKind !== null && (
          <Button size="small" onClick={onIncome} data-testid={`holding-income-${view.id}`}>
            记收益
          </Button>
        )}
        <Button size="small" onClick={onPrice} data-testid={`holding-price-${view.id}`}>
          改价
        </Button>
        <Button size="small" onClick={onHistory} data-testid={`holding-history-${view.id}`}>
          {BTN_HISTORY}
        </Button>
      </Stack>
    </Box>
  );
}
